import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import {
  DollarSign, 
  Filter, 
  RefreshCw,
  Search,
  Shield,
  TrendingUp,
  Calendar,
  Users,
  Battery,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Clock
} from 'lucide-react';

const AUSTRALIA_TIMEZONE = 'Asia/Dubai';

const AllIncomes = () => {
  const CACHE_TTL_MS = 30000;
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [roiWalletBalance, setRoiWalletBalance] = useState(0);
  const [stats, setStats] = useState({
    totalIncome: 0,
    todayIncome: 0,
    levelIncome: 0,
    roiIncome: 0,
    energyIncome: 0,
    blockedIncome: 0,
    pendingROI: 0
  });

  const userId = localStorage.getItem('userId');
  const userDbKey = localStorage.getItem('dbKey');
  const userSponsorId = localStorage.getItem('userSponsorId');
  const userRegistrationDate = localStorage.getItem('registrationDate');

  useEffect(() => {
    if (userId && userDbKey) {
      fetchAllIncomes();
    }
  }, [userId, userDbKey]);

  const getDateKeyInTimezone = (date, timezone = AUSTRALIA_TIMEZONE) => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  };

  const getWeekdayInTimezone = (date, timezone = AUSTRALIA_TIMEZONE) => {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short'
    }).format(date);
  };

  const parseDateKeyToAESTRunDate = (dateKey) => {
    const [year, month, day] = (dateKey || '').split('-').map(Number);
    if (!year || !month || !day) return null;
    // GST midnight = previous day 20:00 UTC
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - (4 * 60 * 60 * 1000));
  };

  // Helper: Check if a day is a working day
  const isWorkingDay = (workingDaysPattern = 'Mon-Fri', date = new Date()) => {
    const dayName = getWeekdayInTimezone(date, AUSTRALIA_TIMEZONE);
    const isWeekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].includes(dayName);
    const isSaturday = dayName === 'Sat';

    if (workingDaysPattern === 'Mon-Fri') return isWeekday;
    if (workingDaysPattern === 'Mon-Sat') return isWeekday || isSaturday;
    if (workingDaysPattern === 'All') return true;
    return isWeekday;
  };

  // Helper: Calculate next working day
  const getNextWorkingDay = (workingDaysPattern = 'Mon-Fri', fromDate = new Date()) => {
    const nextDate = new Date(fromDate);
    nextDate.setDate(nextDate.getDate() + 1);
    
    while (!isWorkingDay(workingDaysPattern, nextDate)) {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    
    return nextDate;
  };

  // Calculate the correct next ROI date
  const calculateNextROIDate = (schedule) => {
    try {
      const now = new Date();
      const workingDaysPattern = schedule.workingDays || 'Mon-Fri';

      if (schedule.nextRoiDateKey) {
        const nextFromKey = parseDateKeyToAESTRunDate(schedule.nextRoiDateKey);
        if (nextFromKey instanceof Date && !Number.isNaN(nextFromKey.getTime())) {
          return nextFromKey;
        }
      }

      if (schedule.nextRoiDate) {
        const nextFromBackend = new Date(schedule.nextRoiDate);
        if (nextFromBackend instanceof Date && !Number.isNaN(nextFromBackend.getTime())) {
          return nextFromBackend;
        }
      }

      const baseDate = new Date(
        schedule.lastRoiDistribution ||
        schedule.startDate ||
        schedule.createdAt ||
        schedule.date ||
        now
      );
      let nextDate = new Date(baseDate);
      nextDate.setDate(nextDate.getDate() + 1);
      nextDate.setHours(0, 0, 0, 0);

      if (!isWorkingDay(workingDaysPattern, nextDate)) {
        nextDate = getNextWorkingDay(workingDaysPattern, nextDate);
      }

      while (nextDate < now) {
        nextDate.setDate(nextDate.getDate() + 1);
        nextDate.setHours(0, 0, 0, 0);
        if (!isWorkingDay(workingDaysPattern, nextDate)) {
          nextDate = getNextWorkingDay(workingDaysPattern, nextDate);
        }
      }

      return nextDate;
    } catch (error) {
      console.error('Error calculating next ROI date:', error);
      return new Date();
    }
  };

  const fetchAllIncomes = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const cacheKey = `all_incomes_cache_${userId || 'guest'}`;
      if (!forceRefresh) {
        const raw = sessionStorage.getItem(cacheKey);
        if (raw) {
          const cached = JSON.parse(raw);
          if (Date.now() - cached.timestamp < CACHE_TTL_MS) {
            setIncomes(cached.incomes || []);
            setRoiWalletBalance(cached.roiWalletBalance || 0);
            setStats(cached.stats || {
              totalIncome: 0,
              todayIncome: 0,
              levelIncome: 0,
              roiIncome: 0,
              energyIncome: 0,
              blockedIncome: 0,
              pendingROI: 0
            });
            setLoading(false);
            return;
          }
        }
      }
      
      // Get today in Dubai GST timezone
      const now = new Date();
      const todayStr = getDateKeyInTimezone(now, AUSTRALIA_TIMEZONE);
      
      console.log('Fetching all incomes for user:', userId);
      
      const allIncomes = [];
      let totalIncome = 0;
      let todayIncome = 0;
      let levelIncome = 0;
      let roiIncome = 0;
      let energyIncome = 0;
      let blockedIncome = 0;
      let pendingROI = 0;

      // ===== 1. Get Wallet Balance (ROI) =====
      let roiWalletBalance = 0;
      if (userRegistrationDate && userDbKey) {
        const userRef = ref(database, `users/${userRegistrationDate}/${userDbKey}`);
        const userSnapshot = await get(userRef);
        
        if (userSnapshot.exists()) {
          const userData = userSnapshot.val();
          roiWalletBalance = parseFloat(userData.wallets?.roi) || 0;
          setRoiWalletBalance(roiWalletBalance);
          
          // Add ROI wallet balance as a summary item
          if (roiWalletBalance > 0) {
            allIncomes.push({
              id: 'roi-wallet-summary',
              date: todayStr,
              displayDate: new Date().toLocaleDateString('en-US', {
                timeZone: AUSTRALIA_TIMEZONE,
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              }),
              type: 'roi',
              subType: 'Accumulated ROI',
              amount: roiWalletBalance,
              status: 'credited',
              fromUser: 'System',
              description: 'Total ROI earned from all packages',
              timestamp: new Date().toISOString(),
              isBlocked: false,
              isSummary: true
            });
            
            roiIncome = roiWalletBalance;
            totalIncome += roiWalletBalance;
          }
        }
      }

      // ===== 2. Fetch Level Incomes from levelIncomes node =====
      try {
        const levelRef = ref(database, 'levelIncomes');
        const levelSnapshot = await get(levelRef);
        
        if (levelSnapshot.exists()) {
          const levelData = levelSnapshot.val();
          
          // Search through all dates
          Object.entries(levelData).forEach(([dateKey, users]) => {
            if (!users) return;
            
            // Check if this user has incomes
            const userIncomes = users[userDbKey] || users[userId] || users[userSponsorId];
            
            if (userIncomes) {
              Object.entries(userIncomes).forEach(([incomeId, income]) => {
                if (income.type === 'level_income') {
                  const amount = parseFloat(income.amount) || 0;
                  const isBlocked = income.status === 'blocked' || income.isBlocked === true;
                  
                  // Parse timestamp
                  let displayDate = dateKey;
                  let displayTime = '';
                  
                  if (income.timestamp || income.date) {
                    try {
                      const incomeDate = new Date(income.timestamp || income.date);
                      displayDate = incomeDate.toLocaleDateString('en-US', {
                        timeZone: AUSTRALIA_TIMEZONE,
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                      displayTime = incomeDate.toLocaleTimeString('en-US', {
                        timeZone: AUSTRALIA_TIMEZONE,
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      });
                    } catch (e) {}
                  }
                  
                  const incomeItem = {
                    id: incomeId,
                    date: dateKey,
                    displayDate: displayDate,
                    displayTime: displayTime,
                    type: 'level',
                    level: income.level || 1,
                    subType: `Level ${income.level || 1}`,
                    amount: amount,
                    status: income.status || 'credited',
                    fromUser: income.fromUserName || income.fromUserId || 'System',
                    fromUserId: income.fromUserId,
                    description: income.description || `Level ${income.level || 1} Commission`,
                    timestamp: income.timestamp || income.date,
                    isBlocked: isBlocked
                  };
                  
                  allIncomes.push(incomeItem);
                  
                  if (!isBlocked) {
                    totalIncome += amount;
                    levelIncome += amount;
                    
                    // Check if today
                    if (dateKey === todayStr) {
                      todayIncome += amount;
                    }
                  } else {
                    blockedIncome += amount;
                  }
                }
              });
            }
          });
        }
      } catch (error) {
        console.error('Error fetching level incomes:', error);
      }

      // ===== 3. Fetch Upcoming ROI from ROISchedule =====
      try {
        const roiScheduleRef = ref(database, 'ROISchedule');
        const scheduleSnapshot = await get(roiScheduleRef);
        
        if (scheduleSnapshot.exists()) {
          const scheduleData = scheduleSnapshot.val();
          
          // Search through all dates
          Object.entries(scheduleData).forEach(([dateKey, dateSchedules]) => {
            if (!dateSchedules) return;
            
            // Look for schedules belonging to this user
            Object.entries(dateSchedules).forEach(([uid, userSchedules]) => {
              if (uid === userId || uid === userDbKey || uid === userSponsorId) {
                Object.entries(userSchedules).forEach(([scheduleId, schedule]) => {
                  if (schedule.status === 'active' && schedule.roiStatus !== 'blocked') {
                    const dailyROIAmount = parseFloat(schedule.dailyROIAmount) || 
                      ((parseFloat(schedule.investmentAmount) || 0) * (parseFloat(schedule.dailyROI) || 0)) / 100;
                    
                    // Calculate the correct next ROI date
                    const correctNextDate = calculateNextROIDate(schedule);
                    const nextDateStr = schedule.nextRoiDateKey || getDateKeyInTimezone(correctNextDate, AUSTRALIA_TIMEZONE);
                    
                    // Format next date for display
                    const formattedNextDate = correctNextDate.toLocaleDateString('en-US', {
                      timeZone: AUSTRALIA_TIMEZONE,
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    });
                    const formattedNextTimeDubai = correctNextDate.toLocaleTimeString('en-US', {
                      timeZone: AUSTRALIA_TIMEZONE,
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true
                    });
                    const formattedNextTime = `${formattedNextTimeDubai} Dubai`;
                    
                    // Calculate if today
                    const isToday = nextDateStr === todayStr;
                    if (isToday) {
                      pendingROI += dailyROIAmount;
                    }
                    
                    // Add as upcoming income
                    allIncomes.push({
                      id: `upcoming_${scheduleId}`,
                      date: nextDateStr,
                      displayDate: formattedNextDate,
                      displayTime: formattedNextTime,
                      type: 'roi',
                      subType: 'Upcoming ROI',
                      amount: dailyROIAmount,
                      status: 'upcoming',
                      fromUser: schedule.packageName || 'Package',
                      description: `Next ROI from ${schedule.packageName || 'package'}`,
                      timestamp: correctNextDate.toISOString(),
                      isBlocked: false,
                      isUpcoming: true,
                      packageName: schedule.packageName
                    });
                  }
                });
              }
            });
          });
        }
      } catch (error) {
        console.error('Error fetching ROI schedules:', error);
      }

      // Sort by timestamp descending (newest first) - but put upcoming at top
      allIncomes.sort((a, b) => {
        if (a.isUpcoming && !b.isUpcoming) return -1;
        if (!a.isUpcoming && b.isUpcoming) return 1;
        
        const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
        const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
        return dateB - dateA;
      });

      console.log(`Found ${allIncomes.length} total income records`);
      console.log('Stats:', { totalIncome, todayIncome, levelIncome, roiIncome, energyIncome, blockedIncome, pendingROI });

      setIncomes(allIncomes);
      setStats({
        totalIncome,
        todayIncome,
        levelIncome,
        roiIncome,
        energyIncome,
        blockedIncome,
        pendingROI
      });

      sessionStorage.setItem(cacheKey, JSON.stringify({
        timestamp: Date.now(),
        incomes: allIncomes,
        roiWalletBalance,
        stats: {
          totalIncome,
          todayIncome,
          levelIncome,
          roiIncome,
          energyIncome,
          blockedIncome,
          pendingROI
        }
      }));
    } catch (error) {
      console.error('Error fetching incomes:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getTypeBadge = (type, subType, isUpcoming) => {
    if (isUpcoming) {
      return {
        label: 'Upcoming ROI',
        color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        icon: <Clock className="h-4 w-4" />
      };
    }
    
    switch(type) {
      case 'level':
        const level = subType?.match(/\d+/) || [1];
        const levelNum = parseInt(level[0]) || 1;
        const levelColors = [
          'bg-blue-500/20 text-blue-400 border-blue-500/30',
          'bg-green-500/20 text-green-400 border-green-500/30',
          'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
          'bg-orange-500/20 text-orange-400 border-orange-500/30',
          'bg-red-500/20 text-red-400 border-red-500/30'
        ];
        return {
          label: `Level ${levelNum}`,
          color: levelColors[Math.min(levelNum - 1, 4)] || levelColors[0],
          icon: <Users className="h-4 w-4" />
        };
      case 'roi':
        return {
          label: subType === 'Accumulated ROI' ? 'Total ROI' : 'Daily ROI',
          color: 'bg-green-500/20 text-green-400 border-green-500/30',
          icon: <TrendingUp className="h-4 w-4" />
        };
      case 'energy':
        return {
          label: 'Energy Wallet',
          color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
          icon: <Battery className="h-4 w-4" />
        };
      default:
        return {
          label: type,
          color: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
          icon: <DollarSign className="h-4 w-4" />
        };
    }
  };

  const getStatusBadge = (status, isBlocked, isUpcoming) => {
    if (isUpcoming) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
          <Clock className="h-3 w-3 mr-1" />
          Upcoming
        </span>
      );
    }
    if (isBlocked || status === 'blocked') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400 border border-red-500/30">
          <Shield className="h-3 w-3 mr-1" />
          Blocked
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400 border border-green-500/30">
        Credited
      </span>
    );
  };

  const filterIncomes = () => {
    let filtered = incomes;
    
    if (dateFilter !== 'all') {
      const now = new Date();
      const australiaNow = new Date(now.toLocaleString('en-US', { timeZone: AUSTRALIA_TIMEZONE }));
      
      filtered = filtered.filter(item => {
        // Don't filter upcoming or summary items by date
        if (item.isUpcoming || item.isSummary) return true;
        
        const itemDate = item.timestamp ? new Date(item.timestamp) : new Date(item.date);
        const australiaItemDate = new Date(itemDate.toLocaleString('en-US', { timeZone: AUSTRALIA_TIMEZONE }));
        
        switch(dateFilter) {
          case 'today':
            return australiaItemDate.toDateString() === australiaNow.toDateString();
          case 'week':
            const weekAgo = new Date(australiaNow);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return australiaItemDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(australiaNow);
            monthAgo.setDate(monthAgo.getDate() - 30);
            return australiaItemDate >= monthAgo;
          default:
            return true;
        }
      });
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.type === typeFilter);
    }
    
    return filtered;
  };

  const filteredIncomes = filterIncomes();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pb-8">
      <h1 className="text-2xl font-bold text-white mb-2">All Incomes</h1>
      <p className="text-gray-400 text-sm mb-6">Your complete income history (Dubai Time)</p>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Total Income</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.totalIncome)}</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Level Income</div>
          <div className="text-lg font-bold text-blue-400">{formatCurrency(stats.levelIncome)}</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">ROI Wallet</div>
          <div className="text-lg font-bold text-green-400">{formatCurrency(roiWalletBalance)}</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Pending ROI</div>
          <div className="text-lg font-bold text-yellow-400">{formatCurrency(stats.pendingROI)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800/30 rounded-xl p-4 mb-6 border border-gray-700">
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm"
          >
            <option value="all">All Dates</option>
            <option value="today">Today</option>
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm"
          >
            <option value="all">All Types</option>
            <option value="level">Level Income</option>
            <option value="roi">ROI Income</option>
          </select>
          
          <button
            onClick={() => fetchAllIncomes(true)}
            className="px-4 py-3 bg-primary hover:bg-primary/80 rounded-lg text-white flex items-center justify-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Incomes List */}
      <div className="space-y-3">
        {filteredIncomes.length > 0 ? (
          filteredIncomes.map((income) => {
            const typeBadge = getTypeBadge(income.type, income.subType, income.isUpcoming);
            const isCredit = !income.isBlocked && !income.isUpcoming;
            
            return (
              <div 
                key={income.id} 
                className={`bg-gray-800/30 rounded-xl p-4 border ${
                  income.isUpcoming ? 'border-yellow-500/30' :
                  income.isBlocked ? 'border-red-500/30' : 'border-gray-700'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg ${
                      income.isUpcoming ? 'bg-yellow-500/20' :
                      isCredit ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}>
                      {income.isUpcoming ? (
                        <Clock className="h-5 w-5 text-yellow-400" />
                      ) : isCredit ? (
                        <ArrowUpRight className="h-5 w-5 text-green-400" />
                      ) : (
                        <ArrowDownRight className="h-5 w-5 text-red-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs border ${typeBadge.color}`}>
                          {typeBadge.icon}
                          <span className="ml-1">{typeBadge.label}</span>
                        </span>
                        {getStatusBadge(income.status, income.isBlocked, income.isUpcoming)}
                      </div>
                      
                      <p className="text-white text-sm mb-1">{income.description}</p>
                      
                      {/* Additional details */}
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs">
                        <span className="text-gray-400">
                          From: {income.fromUser || 'System'}
                        </span>
                        {income.packageName && (
                          <>
                            <span className="hidden sm:inline text-gray-600">•</span>
                            <span className="text-gray-400">
                              {income.packageName}
                            </span>
                          </>
                        )}
                        {!income.isUpcoming && (
                          <>
                            <span className="hidden sm:inline text-gray-600">•</span>
                            <span className="text-gray-400">
                              {income.displayDate} {income.displayTime}
                            </span>
                          </>
                        )}
                        {income.isUpcoming && (
                          <>
                            <span className="hidden sm:inline text-gray-600">•</span>
                            <span className="text-yellow-400">
                              Expected: {income.displayDate} {income.displayTime}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className={`text-lg font-bold ml-4 ${
                    income.isUpcoming ? 'text-yellow-400' :
                    isCredit ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {income.isUpcoming ? '→' : (isCredit ? '+' : '-')}{formatCurrency(income.amount)}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
            <div className="text-5xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-white mb-2">No Income Records</h3>
            <p className="text-gray-400">You haven't received any incomes yet</p>
          </div>
        )}
      </div>

      {/* Summary */}
      {/* {filteredIncomes.length > 0 && (
        <div className="mt-6 p-4 bg-gray-800/30 rounded-xl border border-gray-700">
          <div className="flex justify-between items-center">
            <span className="text-gray-400">Total Records:</span>
            <span className="text-white font-bold">{filteredIncomes.length}</span>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-gray-400">Total Amount (Credited):</span>
            <span className="text-green-400 font-bold">{formatCurrency(
              filteredIncomes.reduce((sum, inc) => sum + (inc.isBlocked || inc.isUpcoming ? 0 : inc.amount), 0)
            )}</span>
          </div>
          {stats.pendingROI > 0 && (
            <div className="flex justify-between items-center mt-2">
              <span className="text-gray-400">Pending Today:</span>
              <span className="text-yellow-400 font-bold">{formatCurrency(stats.pendingROI)}</span>
            </div>
          )}
        </div>
      )} */}
    </div>
  );
};

export default AllIncomes;

